import React, { useEffect, useState } from "react"
import {
  Form,
  Input,
  FormFeedback,
  Label,
  Spinner,
} from "reactstrap"

// Toast notifications
import toastr from "toastr"
import "toastr/build/toastr.min.css"

// Formik Validation
import * as Yup from "yup"
import { useFormik } from "formik"

import { Link, useParams } from "react-router-dom"
import logodark from "../../assets/images/download1.png"
import axios from "axios"
import { api} from "../../utils/variables"
import mainlogo from "../../assets/images/new_web_logo.png";

export default function ResetPassword() {
  const { token } = useParams()
  const [isLoading, setIsLoading] = useState(false)
  const [status, setStatus] = useState({ success: null, message: "" })

  const handleResetPassword = async password => {
    setIsLoading(true)
    try {
      const response = await axios.post(`${api}/user/reset-password/${token}`, {
        password,
      })
      setStatus({
        success: true,
        message: "Your password has been reset successfully!",
      })
    } catch (error) {
      setStatus({
        success: false,
        message: error.response?.data.error || error.message,
      })
    }
    setIsLoading(false)
  }

  useEffect(() => {
    document.body.className = "authentication-bg"
    // remove classname when component will unmount
    return function cleanup() {
      document.body.className = ""
    }
  })

  useEffect(() => {
    // Configure toastr options
    toastr.options = {
      positionClass: "toast-bottom-right",
      timeOut: 5000,
      extendedTimeOut: 1000,
      closeButton: true,
      progressBar: true,
      preventDuplicates: true,
      newestOnTop: true,
      showEasing: "swing",
      hideEasing: "linear",
      showMethod: "fadeIn",
      hideMethod: "fadeOut",
      showDuration: 300,
      hideDuration: 1000
    }
  }, [])

  // Show toast notifications based on status
  useEffect(() => {
    if (status.success === true) {
      toastr.success(status.message, "Password Reset Successful")
      validation.resetForm()
    } else if (status.success === false) {
      toastr.error(status.message, "Password Reset Failed")
      validation.resetForm()
    }
  }, [status])

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .required("Please Enter Your Password")
        .matches(/^(?=.*[A-Z])/, "Password must contain at least one capital letter")
        .matches(/^(?=.*[a-z])/, "Password must contain at least one lowercase letter")
        .matches(/^(?=.*\d)/, "Password must contain at least one number")
        .min(8, "Password must be at least 8 characters long"),
      confirmPassword: Yup.string()
        .required("Please Confirm Your Password")
        .oneOf([Yup.ref("password")], "Passwords must match"),
    }),
    onSubmit: values => {
      handleResetPassword(values.password)
    },
  })

  return (
    <React.Fragment>
      <div className="modern-auth-container">
        <div className="auth-modal">
          {/* Close button */}
          <div className="close-button">
            <Link to="/" className="close-icon">×</Link>
          </div>

          {/* Header */}
          <div className="auth-header">
            <div className="logo-container">
              <img src={mainlogo} alt="KeyQuest" className="logo-image" />
            </div>
            <h1 className="auth-title">Reset Password</h1>
            <p className="auth-subtitle">Enter your new password below</p>
          </div>

          {/* Form */}
          <div className="auth-form">
            <Form
              className="form-horizontal"
              onSubmit={e => {
                e.preventDefault()
                validation.handleSubmit()
                return false
              }}
            >
              <div className="form-group">
                <Label className="form-label">New Password</Label>
                <Input
                  name="password"
                  type="password"
                  className="form-input"
                  placeholder="Enter your new password"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.password || ""}
                  invalid={
                    validation.touched.password &&
                    validation.errors.password
                      ? true
                      : false
                  }
                  disabled={isLoading}
                />
                {validation.touched.password &&
                validation.errors.password ? (
                  <FormFeedback type="invalid">
                    {validation.errors.password}
                  </FormFeedback>
                ) : null}
              </div>

              {/* Confirm Password Field */}
              <div className="form-group">
                <Label className="form-label">Confirm Password</Label>
                <Input
                  name="confirmPassword"
                  type="password"
                  className="form-input"
                  placeholder="Confirm your new password"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.confirmPassword || ""}
                  invalid={
                    validation.touched.confirmPassword &&
                    validation.errors.confirmPassword
                      ? true
                      : false
                  }
                  disabled={isLoading}
                />
                {validation.touched.confirmPassword &&
                validation.errors.confirmPassword ? (
                  <FormFeedback type="invalid">
                    {validation.errors.confirmPassword}
                  </FormFeedback>
                ) : null}
              </div>

              <button
                className={`submit-button ${isLoading ? 'loading' : ''}`}
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Resetting...
                  </>
                ) : (
                  "Reset Password"
                )}
              </button>
            </Form>

            {/* Back to login */}
            <div className="back-to-login">
              <p className="back-text">
                Remember your password?{" "}
                <Link
                  to="/login"
                  className="back-link"
                >
                  Sign in here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .modern-auth-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
          padding: 20px;
          position: relative;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .auth-modal {
          background: #21222D;
          border-radius: 16px;
          padding: 32px;
          width: 100%;
          max-width: 480px;
          position: relative;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
          border: 1px solid #353748;
        }

        .close-button {
          position: absolute;
          top: 20px;
          right: 20px;
        }

        .close-icon {
          color: #A1ACB8;
          font-size: 40px;
          text-decoration: none;
          line-height: 1;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.2s;
        }

        .close-icon:hover {
          background: #3a3a3a;
          color: white;
        }

        .auth-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .logo-container {
          margin-bottom: 16px;
          text-align: center;
        }

        .logo-image {
          width: 185px;
          height: auto;
          object-fit: contain;
          padding-bottom: 15px;
        }

        .auth-title {
          color: #fff;
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 8px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .auth-subtitle {
          color: #E8E8E8;
          font-size: 16px;
          margin: 0;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-weight: 400;
        }

        .auth-form {
          width: 100%;
        }

        .form-group {
          margin-bottom: 24px;
          width: 100%;
        }

        .form-label {
          color: #E8E8E8;
          margin-bottom: 8px;
          display: block;
          font-family: Rubik;
          font-weight: 500;
          font-size: 14px;
          line-height: 21px;
        }

        .form-input {
          width: 100%;
          height: 40px;
          border: 1px solid #353748;
          border-radius: 8px;
          padding: 12px 16px;
          background: #1a1a1a !important;
          color: white !important;
          font-size: 14px !important;
          transition: all 0.2s !important;
        }

        .form-input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .form-input::placeholder {
          color: #666 !important;
          font-family: Rubik;
          font-weight: 400;
          font-size: 16px;
          line-height: 100%;
        }

        .form-input:focus {
          border: 1px solid #353748 !important;
          box-shadow: 0 0 0 2px rgba(233, 30, 99, 0.2) !important;
          outline: none !important;
        }

        .form-input.is-invalid {
          border-color: #dc3545 !important;
          box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.2) !important;
        }

        .form-input.is-invalid:focus {
          border-color: #dc3545 !important;
          box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.2) !important;
        }

        .submit-button {
          width: 100%;
          background: #e91e63;
          border: none;
          color: white;
          padding: 14px 24px;
          border-radius: 20px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          margin-bottom: 24px;
          font-family: Rubik;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .submit-button:hover:not(:disabled) {
          background: #F6255A;
        }

        .submit-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .submit-button.loading {
          background: #F6255A;
        }

        .back-to-login {
          text-align: center;
          margin-top: 16px;
        }

        .back-text {
          color: #E8E8E8;
          font-size: 14px;
          margin: 0;
          font-family: Rubik;
          font-weight: 400;
        }

        .back-link {
          color: #F6255A;
          text-decoration: none;
          font-weight: 500;
        }

        .back-link:hover {
          text-decoration: underline;
        }

        /* Error message styling */
        .error-message {
          color: #dc3545 !important;
          font-size: 12px !important;
          margin-top: 4px !important;
          display: block !important;
          font-family: Rubik !important;
          font-weight: 400 !important;
        }

        @media (max-width: 480px) {
          .auth-modal {
            padding: 24px;
            margin: 0;
          }
          
          .modern-auth-container {
            padding: 16px;
          }
          
          .auth-title {
            font-size: 24px;
          }
          
          .auth-subtitle {
            font-size: 14px;
          }
        }
      `}</style>
    </React.Fragment>
  )
}
